/**
 * Radio Card Field Component
 * Renders a group of card-styled radio options with react-hook-form integration.
 *
 * Registered as FieldType.RADIO_CARD, so it inherits everything the other
 * FormControls get for free: buildValidationRules(), setFieldError(), data
 * sources, linkage rules, visible() and participation in resetForm().
 */
import React, { useMemo } from 'react'
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'
import { Controller } from 'react-hook-form'
import { FormFieldProps, FieldOption } from '../types'
import { useFormContext } from '../FormContext'
import { buildValidationRules } from '../utils/validationRules'

const RadioCardField: React.FC<FormFieldProps> = ({
  config,
  control,
  errors,
  readOnly
}) => {
  const {
    name,
    label,
    options = [],
    required,
    disabled,
    helperText,
    dataSource,
    fieldProps = {}
  } = config

  // get the data source data
  const formContext = useFormContext()
  const dataSources = formContext?.dataSources || {}
  const dataSourceState = dataSource ? dataSources[dataSource] : null

  // merge the options: use the options from the data source first, if there is
  // no data source or the data source is not loaded, use the options from the config
  const mergedOptions = useMemo<FieldOption[]>(() => {
    if (
      dataSource &&
      dataSourceState?.options &&
      dataSourceState.options.length > 0
    ) {
      return dataSourceState.options
    }
    return options
  }, [dataSource, dataSourceState, options])

  const {
    row = true,
    descriptions = {},
    sx: fieldSx,
    ...otherRadioGroupProps
  } = fieldProps

  // check if the field is read only — readOnly is either a boolean for the whole
  // form or an array of field names
  const isReadOnly =
    typeof readOnly === 'boolean'
      ? readOnly
      : Array.isArray(readOnly)
        ? readOnly.includes(name)
        : false

  // check if the data source is loading
  const isLoading = Boolean(dataSource && dataSourceState?.loading === true)

  const isDisabled = Boolean(disabled) || isReadOnly || isLoading

  // handle the data source error
  //
  // Defensive coercion: every branch must produce a string. A data source error
  // is frequently the API client's `NetworkError` object (`{type, code, message,
  // additional}`) — handing that straight to a React child crashes with
  // "Objects are not valid as a React child". Same guard as AutoCompleteField.
  const dsErr =
    dataSource && dataSourceState?.error ? dataSourceState.error : null
  const dsErrMessage = dsErr
    ? (typeof dsErr === 'string' ? dsErr : dsErr?.message || dsErr?.msg) ||
      'Failed to load options'
    : null

  const rhfErrMessage =
    typeof errors?.[name]?.message === 'string'
      ? (errors[name].message as string)
      : undefined

  const helperTextString =
    typeof helperText === 'string' ? helperText : undefined

  const rawMessage: any = rhfErrMessage || dsErrMessage || helperTextString
  const errorMessage =
    typeof rawMessage === 'string'
      ? rawMessage
      : String(rawMessage?.message ?? rawMessage ?? '')

  const hasError = Boolean(errors?.[name]) || Boolean(dsErr)

  const labelId = `${name}-radio-card-label`

  return (
    <Controller
      name={name}
      control={control}
      rules={buildValidationRules(config)}
      render={({ field }) => {
        // Never let the value be undefined — that flips the group between
        // uncontrolled and controlled and logs a React warning.
        const fieldValue = field.value === undefined ? '' : field.value

        // Values round-trip as strings: `0` / `'0'` / `30` / `'30'` must all
        // match. `0` is a real, selectable value here, so never truthiness-test.
        const groupValue = fieldValue === '' ? '' : String(fieldValue)

        return (
          <FormControl
            component='fieldset'
            fullWidth
            required={required}
            error={hasError}
            disabled={isDisabled}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {label ? (
                <FormLabel component='legend' id={labelId}>
                  {label}
                </FormLabel>
              ) : null}
              {isLoading ? <CircularProgress size={16} /> : null}
            </Box>
            <RadioGroup
              row={row}
              aria-labelledby={label ? labelId : undefined}
              name={name}
              value={groupValue}
              onChange={event => {
                // `pointerEvents: none` and the disabled input are CSS/DOM
                // affordances only — a synthesised or programmatic click still
                // reaches this handler. Gate the write, not just the paint.
                if (isDisabled) return
                field.onChange(String(event.target.value))
              }}
              sx={{
                gap: 1,
                mt: label ? 1 : 0,
                flexWrap: 'wrap',
                ...(fieldSx || {})
              }}
              {...otherRadioGroupProps}>
              {mergedOptions.map((option: FieldOption) => {
                const isSelected = String(fieldValue) === String(option.value)
                const description = descriptions?.[option.value]

                return (
                  <Paper
                    key={option.value}
                    variant='outlined'
                    elevation={0}
                    data-testid={`radio-card-${name}-${option.value}`}
                    data-selected={isSelected ? 'true' : 'false'}
                    sx={{
                      flex: row ? '1 1 0' : '1 1 auto',
                      minWidth: 0,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isSelected
                        ? 'action.selected'
                        : 'background.paper',
                      pointerEvents: isDisabled ? 'none' : 'auto',
                      opacity: isDisabled ? 0.6 : 1
                    }}>
                    <FormControlLabel
                      value={String(option.value)}
                      checked={isSelected}
                      disabled={isDisabled}
                      control={<Radio size='small' />}
                      label={
                        <Box>
                          <Typography variant='body2' component='span'>
                            {option.label}
                          </Typography>
                          {description ? (
                            <Typography
                              variant='caption'
                              component='span'
                              color='text.secondary'
                              sx={{ display: 'block' }}>
                              {description}
                            </Typography>
                          ) : null}
                        </Box>
                      }
                      sx={{
                        m: 0,
                        px: 1.5,
                        py: 1,
                        width: '100%',
                        alignItems: 'flex-start'
                      }}
                    />
                  </Paper>
                )
              })}
            </RadioGroup>
            {errorMessage ? (
              <FormHelperText error={hasError}>{errorMessage}</FormHelperText>
            ) : null}
          </FormControl>
        )
      }}
    />
  )
}

export default RadioCardField
